import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  AccordionSummary,
  Accordion,
  Grid2,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import {
  Eye,
  FileText,
  Users,
  School,
  Clock,
  CheckCircle,
  AlertTriangle,
  ArrowBigUp,
  Mail,
} from 'lucide-react';
import { Project } from '../../types/project.types';
import { FormSubmission, ReviewStatus } from '../../types/forms.types';
import FormReview from './forms/FormReview';
import { useAuth } from '../../contexts/AuthContext';
import { questionnaireService } from '../../services/questionnaire.service';
import { getRegionById } from '../../services/regions.services';
import { usersService } from '../../services/users.service';
import { Region } from '../../types/fair.types';
import { Questionnaire } from '../../types/questionnaire.types';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

interface ProjectReviewDialogProps {
  open: boolean;
  onClose: () => void;
  project: Project & { forms: FormSubmission[] };
  onUpdateProjectStatus: (
    projectId: string,
    status: ReviewStatus,
    comments?: string
  ) => Promise<void>;
  onSubmitFormReview: (
    formId: string,
    status: ReviewStatus,
    comments: string
  ) => Promise<void>;
  onAssignReviewer: (
    formId: string,
    reviewerId: string,
    reviewerName: string,
    role: 'primary' | 'secondary' | 'final'
  ) => Promise<void>;
}

const ProjectReviewDialog: React.FC<ProjectReviewDialogProps> = ({
  open,
  onClose,
  project,
  onUpdateProjectStatus,
  onSubmitFormReview,
  onAssignReviewer,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedForm, setSelectedForm] = useState<FormSubmission | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [projectStatus, setProjectStatus] = useState<ReviewStatus>('pending');
  const [projectComments, setProjectComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([]);
  const [region, setRegion] = useState<Region | null>(null);
  const { authStatus } = useAuth();

  // Email tab state
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState<Set<string>>(
    new Set()
  );
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState('');

  const fetchUserRegion = useCallback(async () => {
    try {
      const userId = authStatus.user?.uid;

      if (!userId) {
        setError('User information not available.');

        return;
      }

      const userRegionId = await usersService.getUserRegion(userId);
      const userRegion = await getRegionById(userRegionId);

      if (!userRegion) {
        setError('You are not associated with any region.');

        return;
      }

      setRegion({ ...userRegion });
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    }
  }, [authStatus.user?.uid]);

  useEffect(() => {
    fetchUserRegion();
  }, [fetchUserRegion]);

  const fetchQuestionnaires = useCallback(async () => {
    try {
      if (region?.id && project.fairId) {
        const subscribe =
          questionnaireService.subscribeToActiveQuestionnairesByFair(
            (updatedQuestionnaires) => {
              setQuestionnaires(updatedQuestionnaires);
            },
            project.fairId
          );
        return () => subscribe();
      }
    } catch (error) {
      console.error('Error fetching regions:', error);
      setError('Failed to load region information');
    } finally {
    }
  }, [region?.id, project.fairId]);

  useEffect(() => {
    fetchQuestionnaires();
  }, [region, fetchQuestionnaires]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'rejected':
      case 'needs_revision':
        return <AlertTriangle size={16} className="text-yellow-500" />;
      default:
        return <Clock size={16} className="text-blue-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'needs_revision':
        return 'warning';
      case 'in_review':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleUpdateProject = async () => {
    try {
      setIsSubmitting(true);
      setError(null);
      await onUpdateProjectStatus(project.id, projectStatus, projectComments);
      onClose();
    } catch (err) {
      setError('Failed to update project status');
      console.error('Project update error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const areAllFormsReviewed = project.forms.every(
    (form) => form.status === 'approved' || form.status === 'rejected'
  );

  const getQuestionnaireSubmission = (questionnaireID: string) => {
    return project.forms.filter((obj) => obj.formType === questionnaireID);
  };

  const getQuestionnaireStatus = (questionnaireID: string) => {
    var form = getQuestionnaireSubmission(questionnaireID)[0];
    if (form) {
      return form.status;
    } else {
      return 'No Submissions';
    }
  };

  // Get all project members (students and sponsor)
  const getProjectRecipients = () => {
    const recipients: { email: string; name: string; type: string }[] = [];

    // Add students
    project.members.forEach((member) => {
      recipients.push({
        email: member.email,
        name: `${member.firstName} ${member.lastName}`,
        type: 'Student',
      });
    });

    // Add sponsor
    if (project.adultSponsor?.email) {
      recipients.push({
        email: project.adultSponsor.email,
        name: project.adultSponsor.name,
        type: 'Sponsor',
      });
    }

    return recipients;
  };

  const handleToggleRecipient = (email: string) => {
    const newSelected = new Set(selectedRecipients);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedRecipients(newSelected);
  };

  const handleSelectAllRecipients = () => {
    const allEmails = getProjectRecipients().map((r) => r.email);
    setSelectedRecipients(new Set(allEmails));
  };

  const handleDeselectAllRecipients = () => {
    setSelectedRecipients(new Set());
  };

  const handleSendEmail = async () => {
    if (selectedRecipients.size === 0 || !emailBody.trim()) {
      setError('Please select recipients and enter email body');
      return;
    }

    setSendingEmail(true);
    setError(null);
    setEmailSuccess('');

    try {
      const recipients = getProjectRecipients()
        .filter((r) => selectedRecipients.has(r.email))
        .map((r) => ({ email: r.email, name: r.name }));

      const sendBulkEmail = httpsCallable(functions, 'sendBulkEmail');
      await sendBulkEmail({
        recipients,
        subject: emailSubject || '(No Subject)',
        body: emailBody,
      });

      setEmailSuccess(
        `Email sent successfully to ${recipients.length} recipient${recipients.length !== 1 ? 's' : ''}!`
      );

      // Reset form
      setEmailSubject('');
      setEmailBody('');
      setSelectedRecipients(new Set());

      // Clear success message after 5 seconds
      setTimeout(() => {
        setEmailSuccess('');
      }, 5000);
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Project Review: {project.name}</Typography>
            <Chip
              size="small"
              icon={getStatusIcon(project.status as ReviewStatus)}
              label={project.status.replace('_', ' ')}
              color={getStatusColor(project.status as ReviewStatus)}
            />
          </Box>
        </DialogTitle>

        <DialogContent>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab icon={<FileText size={18} />} label="Forms" />
            <Tab icon={<Users size={18} />} label="Team" />
            <Tab icon={<School size={18} />} label="Details" />
            <Tab icon={<Mail size={18} />} label="Email" />
          </Tabs>

          {activeTab === 0 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Project Forms ({project.forms.length})
              </Typography>
              {!areAllFormsReviewed && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  All forms must be reviewed before updating project status
                </Alert>
              )}
              <Box className="space-y-4">
                {questionnaires.map(
                  (questionnaire) =>
                    questionnaire && (
                      <Box>
                        <Accordion
                          key={questionnaire.id}
                          elevation={2}
                          defaultExpanded
                        >
                          <AccordionSummary
                            expandIcon={<ArrowBigUp />}
                            aria-controls="panel1-content"
                            id="panel1-header"
                          >
                            <Grid2 container spacing={1} sx={{ width: '98%' }}>
                              <Grid2
                                display="flex"
                                justifyContent="left"
                                alignItems="center"
                                size="grow"
                              >
                                <Typography variant="h6" className="mb-2">
                                  {questionnaire.title}
                                </Typography>
                              </Grid2>
                              <Grid2
                                display="flex"
                                justifyContent="right"
                                alignItems="center"
                              >
                                <Chip
                                  size="small"
                                  icon={getStatusIcon(
                                    getQuestionnaireStatus(questionnaire.id)
                                  )}
                                  label={getQuestionnaireStatus(
                                    questionnaire.id
                                  ).replace('_', ' ')}
                                  color={getStatusColor(
                                    getQuestionnaireStatus(questionnaire.id)
                                  )}
                                />
                              </Grid2>
                            </Grid2>
                          </AccordionSummary>
                          <AccordionDetails>
                            <List>
                              {getQuestionnaireSubmission(questionnaire.id)
                                .length >= 1 &&
                                getQuestionnaireSubmission(
                                  questionnaire.id
                                ).map((form) => (
                                  <ListItem key={form.id}>
                                    <ListItemText
                                      primary={
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                          }}
                                        >
                                          <Typography>
                                            {`Submission ${form.versions.length}`}
                                          </Typography>
                                          {form.isRequired && (
                                            <Chip
                                              size="small"
                                              color="error"
                                              label="Required"
                                            />
                                          )}
                                        </Box>
                                      }
                                      secondary={
                                        <>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Responsible Student:{' '}
                                            {
                                              form.projectContext
                                                .responsibleStudentName
                                            }
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Last updated:{' '}
                                            {new Date(
                                              form.updatedAt
                                            ).toLocaleString()}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            Reviews:{' '}
                                            {
                                              form.versions[form.currentVersion]
                                                .reviews.length
                                            }
                                          </Typography>
                                        </>
                                      }
                                    />
                                    <ListItemSecondaryAction>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          gap: 1,
                                          alignItems: 'center',
                                        }}
                                      >
                                        <Chip
                                          size="small"
                                          icon={getStatusIcon(form.status)}
                                          label={form.status.replace('_', ' ')}
                                          color={getStatusColor(form.status)}
                                        />

                                        <IconButton
                                          size="small"
                                          onClick={() => setSelectedForm(form)}
                                          title="Review Form"
                                        >
                                          <Eye size={18} />
                                        </IconButton>
                                      </Box>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                ))}
                              {!(
                                getQuestionnaireSubmission(questionnaire.id)
                                  .length >= 1
                              ) && <div>No Submissions</div>}
                            </List>
                          </AccordionDetails>
                        </Accordion>
                      </Box>
                    )
                )}
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mt: 4 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Project Review
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Project Status</InputLabel>
                  <Select
                    value={projectStatus}
                    label="Project Status"
                    onChange={(e) =>
                      setProjectStatus(e.target.value as ReviewStatus)
                    }
                    disabled={!areAllFormsReviewed}
                  >
                    <MenuItem value="pending">Pending Review</MenuItem>
                    <MenuItem value="in_review">In Review</MenuItem>
                    <MenuItem value="needs_revision">Needs Revision</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Review Comments"
                  value={projectComments}
                  onChange={(e) => setProjectComments(e.target.value)}
                  disabled={!areAllFormsReviewed}
                />
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Team Members
              </Typography>
              <List>
                {project.members.map((member) => (
                  <ListItem key={member.userId}>
                    <ListItemText
                      primary={`${member.firstName} ${member.lastName}`}
                      secondary={member.email}
                    />
                    <Chip
                      size="small"
                      label={member.role}
                      color={member.role === 'creator' ? 'primary' : 'default'}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Project Details
              </Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary="Fair Type"
                    secondary={
                      project.fairType === 'highSchool'
                        ? 'High School'
                        : 'Middle School'
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Project Code"
                    secondary={project.projectCode}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Adult Sponsor"
                    secondary={
                      <Box>
                        <Typography variant="body2">
                          {project.adultSponsor.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {project.adultSponsor.email}
                        </Typography>
                        {project.adultSponsor.isTeacher && (
                          <Chip
                            size="small"
                            label="Teacher"
                            color="primary"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Send Email to Project Members
              </Typography>

              {emailSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {emailSuccess}
                </Alert>
              )}

              {error && activeTab === 3 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Recipients Selection */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography variant="body1" fontWeight="medium">
                    Recipients ({selectedRecipients.size} selected)
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          selectedRecipients.size ===
                            getProjectRecipients().length &&
                          getProjectRecipients().length > 0
                        }
                        indeterminate={
                          selectedRecipients.size > 0 &&
                          selectedRecipients.size <
                            getProjectRecipients().length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleSelectAllRecipients();
                          } else {
                            handleDeselectAllRecipients();
                          }
                        }}
                        disabled={sendingEmail}
                      />
                    }
                    label="Select All"
                  />
                </Box>

                <List
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
                >
                  {getProjectRecipients().map((recipient) => (
                    <ListItem
                      key={recipient.email}
                      sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '&:last-child': { borderBottom: 0 },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedRecipients.has(recipient.email)}
                            onChange={() =>
                              handleToggleRecipient(recipient.email)
                            }
                            disabled={sendingEmail}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1">
                              {recipient.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {recipient.email}
                            </Typography>
                          </Box>
                        }
                      />
                      <Chip
                        size="small"
                        label={recipient.type}
                        color={
                          recipient.type === 'Student' ? 'primary' : 'secondary'
                        }
                        sx={{ ml: 'auto' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>

              {/* Email Composition */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  disabled={sendingEmail}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  multiline
                  rows={8}
                  disabled={sendingEmail}
                  required
                  helperText="Enter your email message"
                />
              </Box>

              {/* Send Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={
                    sendingEmail ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Mail size={20} />
                    )
                  }
                  onClick={handleSendEmail}
                  disabled={
                    selectedRecipients.size === 0 ||
                    !emailBody.trim() ||
                    sendingEmail
                  }
                >
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
          {activeTab === 0 && (
            <Button
              variant="contained"
              onClick={handleUpdateProject}
              disabled={!areAllFormsReviewed || isSubmitting}
            >
              Update Project Status
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Form Review Dialog */}
      {selectedForm && (
        <FormReview
          open={Boolean(selectedForm)}
          onClose={() => setSelectedForm(null)}
          form={selectedForm}
          onSubmitReview={onSubmitFormReview}
          onAssignReviewer={onAssignReviewer}
        />
      )}
    </>
  );
};

export default ProjectReviewDialog;
